import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { format } from "date-fns";
import { ArrowLeft, Copy, MessageSquare, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";
import { Layout } from "@/components/Layout/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar } from "@/components/User/Avatar";
import { NeonUpvoteIcon } from "@/components/ui/NeonUpvoteIcon";
import { GeoBadges } from "@/components/GEO/GeoBadges";
import { GeoQualityBadge } from "@/components/GEO/GeoQualityBadge";
import { GeoReliabilityActions } from "@/components/GEO/GeoReliabilityActions";
import { CommentSection } from "@/components/Comments/CommentSection";
import { Skeleton } from "@/components/ui/skeleton";
import { createPromptSchema, createCreativeWorkSchema } from "@/lib/prompt-schema";
import { calculateGeoQualityScore } from "@/lib/geo-quality-score";
import { Prompt } from "@/lib/types";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { usePromptActions } from "@/hooks/prompts/use-prompt-actions";
import { toDatabaseValue } from "@/utils/supabase/type-guards";
import { getEmbeddedPublicProfile } from "@/utils/public-profiles";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const PromptPage = () => {
  const { promptId } = useParams<{ promptId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [loading, setLoading] = useState(true);
  const [copyCount, setCopyCount] = useState(0);

  useEffect(() => {
    const fetchPrompt = async () => {
      if (!promptId) return;
      
      try {
        const { data, error } = await supabase
          .from("prompts")
          .select(`
            *,
            public_profiles:user_id (
              username,
              avatar_id
            )
          `)
          .eq("id", promptId)
          .single();

        if (error) throw error;

        // Get counts
        const [likesResult, commentsResult] = await Promise.all([
          supabase.from("prompt_likes").select("id", { count: "exact" }).eq("prompt_id", promptId),
          supabase.from("prompt_comments").select("id", { count: "exact" }).eq("prompt_id", promptId),
        ]);

        // Check if current user liked
        let isLiked = false;
        if (user) {
          const { data: likeData } = await supabase
            .from("prompt_likes")
            .select("id")
            .eq("prompt_id", promptId)
            .eq("user_id", user.id)
            .maybeSingle();
          isLiked = !!likeData;
        }

        const embeddedProfile = getEmbeddedPublicProfile(data.public_profiles);

        const promptData: Prompt = {
          id: data.id,
          userId: data.user_id,
          username: embeddedProfile?.username || "Unknown",
          avatarId: embeddedProfile?.avatar_id || 1,
          title: data.title || "",
          content: data.content || "",
          description: data.description,
          category: (data.category || "general-chat") as Exclude<import("@/lib/types").Category, 'all'>,
          likes: likesResult.count || 0,
          comments: commentsResult.count || 0,
          createdAt: data.created_at,
          copyCount: data.copy_count || 0,
          isLiked,
          geoMetadata: data.geo_metadata as any,
        };

        setPrompt(promptData);
        setCopyCount(promptData.copyCount || 0);
      } catch (error) {
        console.error("Error fetching prompt:", error);
        toast.error("Failed to load prompt");
      } finally {
        setLoading(false);
      }
    };

    fetchPrompt();
  }, [promptId, user]);

  const handleCopy = async () => {
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt.content);
      await supabase.rpc("increment_copy_count", {
        prompt_id: toDatabaseValue(prompt.id),
      });
      setCopyCount((prev) => prev + 1);
      toast.success("Copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const handleUpdate = (id: string, updates: Partial<Prompt>) => {
    if (prompt && prompt.id === id) {
      setPrompt({ ...prompt, ...updates });
    }
  };

  const { isLiked, likeCount, handleLike } = usePromptActions(
    prompt || ({} as Prompt),
    user,
    handleUpdate
  );

  if (loading) {
    return (
      <Layout>
        <div className="container max-w-4xl mx-auto py-8 px-4">
          <Skeleton className="h-8 w-48 mb-6" />
          <Skeleton className="h-64 w-full mb-4" />
          <Skeleton className="h-32 w-full" />
        </div>
      </Layout>
    );
  }

  if (!prompt) {
    return (
      <Layout>
        <div className="container max-w-4xl mx-auto py-8 px-4 text-center">
          <h1 className="text-2xl font-bold mb-4">Prompt not found</h1>
          <Button onClick={() => navigate("/prompts")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Prompts
          </Button>
        </div>
      </Layout>
    );
  }

  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const qualityScore = calculateGeoQualityScore(prompt);

  return (
    <Layout>
      <Helmet>
        <title>{prompt.title || "AI Prompt"} | ThePromptSpace</title>
        <meta 
          name="description" 
          content={prompt.description || prompt.content.slice(0, 160)} 
        />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={prompt.title || "AI Prompt"} />
        <meta property="og:description" content={prompt.description || prompt.content.slice(0, 160)} />
        <meta property="og:url" content={`${baseUrl}/prompts/${prompt.id}`} />
        <link rel="canonical" href={`${baseUrl}/prompts/${prompt.id}`} />
        
        {/* JSON-LD Structured Data for AI Search Engines */}
        <script type="application/ld+json">
          {JSON.stringify(createPromptSchema(prompt, baseUrl))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(createCreativeWorkSchema(prompt, baseUrl))}
        </script>
      </Helmet>

      <div className="container max-w-4xl mx-auto py-8 px-4">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card/50 backdrop-blur-md rounded-2xl border border-border/50 overflow-hidden"
        >
          {/* Header */}
          <div className="p-6 border-b border-border/50">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div 
                className="flex items-center gap-3 cursor-pointer"
                onClick={() => navigate(`/users/${prompt.userId}`)}
              >
                <Avatar
                  userId={prompt.userId}
                  avatarId={prompt.avatarId}
                  size="lg"
                />
                <div>
                  <h4 className="font-semibold text-foreground hover:text-brand-cyan transition-colors">
                    @{prompt.username}
                  </h4>
                  <span className="text-sm text-muted-foreground">
                    {format(new Date(prompt.createdAt), "MMMM d, yyyy")}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <GeoQualityBadge qualityScore={qualityScore} />
                {prompt.category && (
                  <Badge variant="secondary">
                    {prompt.category.replace(/-/g, " ")}
                  </Badge>
                )}
              </div>
            </div>

            {/* GEO Badges */}
            {prompt.geoMetadata && (
              <div className="mb-4">
                <GeoBadges geoMetadata={prompt.geoMetadata} />
              </div>
            )}

            {/* Title */}
            {prompt.title && (
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                {prompt.title}
              </h1>
            )}

            {/* Description */}
            {prompt.description && (
              <p className="text-muted-foreground">
                {prompt.description}
              </p>
            )}
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="relative">
              <pre className="whitespace-pre-wrap font-mono text-sm bg-muted/30 rounded-lg p-4 border border-border/30">
                {prompt.content}
              </pre>
              <Button
                size="sm"
                variant="secondary"
                className="absolute top-2 right-2"
                onClick={handleCopy}
              >
                <Copy className="h-4 w-4 mr-1" />
                Copy
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div className="px-6 pb-6 flex items-center justify-between border-t border-border/50 pt-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleLike(!isLiked)}
                className={cn(
                  "gap-2",
                  isLiked && "text-brand-cyan"
                )}
              >
                <NeonUpvoteIcon className="h-4 w-4" isActive={isLiked} />
                <span>{likeCount}</span>
              </Button>

              <div className="flex items-center gap-1.5 text-muted-foreground text-sm">
                <MessageSquare className="h-4 w-4" />
                <span>{prompt.comments}</span>
              </div>

              <div className="flex items-center gap-1.5 text-muted-foreground text-sm">
                <Copy className="h-4 w-4" />
                <span>{copyCount}</span>
              </div>
            </div>

            {/* GEO Reliability Actions */}
            {prompt.geoMetadata && (
              <GeoReliabilityActions
                promptId={prompt.id}
                worksCount={prompt.geoMetadata.reliability?.community_verified || 0}
                issueCount={prompt.geoMetadata.reliability?.reported_issues || 0}
              />
            )}
          </div>
        </motion.article>

        {/* Comments Section */}
        <div className="mt-8">
          <CommentSection promptId={prompt.id} />
        </div>
      </div>
    </Layout>
  );
};

export default PromptPage;
